import type { ComponentType, ReactNode } from 'react';
import { Card, Link } from '@mokoboko/components';

import { Illustration } from '@/components/illustration';
import { PoweredByTimitomi } from '@/components/powered-by-timitomi';
import type { IllustrationName } from '@/lib/illustrations';

type PageStateProps = {
  illustration: IllustrationName;
  title: string;
  description: ReactNode;
  label?: string;
  header?: ReactNode;
  actions?: ReactNode;
  aside?: ReactNode;
  poweredBy?: boolean;
};

export const PageState = ({
  illustration,
  title,
  description,
  label,
  header,
  actions,
  aside,
  poweredBy = true,
}: PageStateProps) => (
  <div className="flex w-full max-w-[440px] flex-col items-center text-center">
    {header}
    <Illustration
      name={illustration}
      alt=""
      width={128}
      height={128}
      quality={100}
      priority
      className="h-32 w-32 object-contain"
    />
    <div className="mt-6 flex flex-col items-center gap-2">
      {label && (
        <p className="text-xs font-semibold uppercase tracking-wider text-ground-muted">
          {label}
        </p>
      )}
      <h1 className="whitespace-pre-line text-balance font-display text-xl font-bold text-ground-heading sm:text-2xl">
        {title}
      </h1>
      <p className="max-w-[380px] whitespace-pre-line text-balance text-sm text-ground-body sm:text-base">
        {description}
      </p>
    </div>
    {actions && (
      <div className="on-ground mt-8 flex w-full max-w-xs flex-col gap-3">
        {actions}
      </div>
    )}
    {aside}
    {poweredBy && <PoweredByTimitomi />}
  </div>
);

type PageStateOwnerCardProps = {
  icon: ComponentType<{ className?: string }>;
  title: string;
  description: string;
  href: string;
  linkLabel: string;
};

export const PageStateOwnerCard = ({
  icon: Icon,
  title,
  description,
  href,
  linkLabel,
}: PageStateOwnerCardProps) => (
  <Card
    className="mb-2 mt-10 w-full text-left sm:w-[520px]"
    contentClassName="flex items-start gap-3 p-4 sm:items-center"
  >
    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-gray-500 sm:mt-0" />
    <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-balance text-sm font-semibold text-gray-900">
          {title}
        </p>
        <p className="text-balance text-sm text-gray-700">{description}</p>
      </div>
      <Link
        href={href}
        size="sm"
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 self-start sm:self-auto"
      >
        {linkLabel}
      </Link>
    </div>
  </Card>
);
